/**
 * File processing utilities for image upload and validation
 * Stage 5: File Upload & Processing
 */

export interface FileValidationOptions {
  maxSize?: number; // in bytes
  acceptedTypes?: string[];
  maxWidth?: number;
  maxHeight?: number;
}

export interface ProcessedFile {
  file: File;
  base64: string;
  dataUrl: string;
  width: number;
  height: number;
  size: number;
  type: string;
}

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1 for JPEG
  outputFormat?: 'jpeg' | 'png' | 'webp';
}

export class FileProcessor {
  private static readonly DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  private static readonly DEFAULT_MAX_DIMENSION = 2048;

  /**
   * Validate a file against specified criteria
   */
  static validateFile(
    file: File, 
    options: FileValidationOptions = {}
  ): { isValid: boolean; error?: string } {
    const {
      maxSize = this.DEFAULT_MAX_SIZE,
      acceptedTypes = this.DEFAULT_ACCEPTED_TYPES
    } = options;

    // Check file type
    if (!acceptedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File type ${file.type} is not supported. Please upload: ${acceptedTypes.join(', ')}`
      };
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      const fileSizeMB = Math.round(file.size / (1024 * 1024));
      return {
        isValid: false,
        error: `File size (${fileSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB`
      };
    }

    return { isValid: true };
  }

  /**
   * Convert file to base64 string
   */
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get pure base64
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert file to data URL (includes MIME type prefix)
   */
  static async fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get image dimensions from file
   */
  static async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  /**
   * Compress image if it's too large
   */
  static async compressImage(
    file: File, 
    options: CompressionOptions = {}
  ): Promise<{ file: File; wasCompressed: boolean }> {
    const {
      maxWidth = this.DEFAULT_MAX_DIMENSION,
      maxHeight = this.DEFAULT_MAX_DIMENSION,
      quality = 0.8,
      outputFormat = 'jpeg'
    } = options;

    try {
      const dimensions = await this.getImageDimensions(file);
      const needsResize = dimensions.width > maxWidth || dimensions.height > maxHeight;
      const needsCompression = file.size > this.DEFAULT_MAX_SIZE / 2; // Compress if over 5MB

      if (!needsResize && !needsCompression) {
        return { file, wasCompressed: false };
      }

      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          URL.revokeObjectURL(url);

          // Calculate new dimensions maintaining aspect ratio
          let { width, height } = dimensions;
          if (needsResize) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress
          ctx?.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Create new file from blob
              const compressedFile = new File(
                [blob], 
                file.name.replace(/\.[^/.]+$/, `.${outputFormat}`),
                { 
                  type: `image/${outputFormat}`,
                  lastModified: Date.now()
                }
              );

              resolve({ file: compressedFile, wasCompressed: true });
            },
            `image/${outputFormat}`,
            quality
          );
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image for compression'));
        };

        img.src = url;
      });
    } catch (error) {
      // If compression fails, return original file
      console.warn('Image compression failed, using original:', error);
      return { file, wasCompressed: false };
    }
  }

  /**
   * Process file completely: validate, compress if needed, and convert to base64
   */
  static async processFile(
    file: File,
    validationOptions: FileValidationOptions = {},
    compressionOptions: CompressionOptions = {}
  ): Promise<ProcessedFile> {
    // Validate file
    const validation = this.validateFile(file, validationOptions);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    try {
      // Compress if needed
      const { file: processedFile } = await this.compressImage(file, compressionOptions);
      
      // Get final dimensions
      const dimensions = await this.getImageDimensions(processedFile);
      
      // Convert to base64 and data URL
      const [base64, dataUrl] = await Promise.all([
        this.fileToBase64(processedFile),
        this.fileToDataUrl(processedFile)
      ]);

      return {
        file: processedFile,
        base64,
        dataUrl,
        width: dimensions.width,
        height: dimensions.height,
        size: processedFile.size,
        type: processedFile.type
      };
    } catch (error) {
      throw new Error(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Check if file is an image
   */
  static isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Get file extension from MIME type
   */
  static getFileExtension(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif',
      'image/bmp': 'bmp',
      'image/tiff': 'tiff'
    };
    return mimeToExt[mimeType] || 'unknown';
  }

  /**
   * Create a preview URL for file (for displaying thumbnails)
   */
  static createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke a preview URL to free memory
   */
  static revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  /**
   * Validate if an image URL is accessible
   * @param url - Image URL to validate
   * @param timeout - Timeout in milliseconds (default: 5000)
   * @returns Promise<boolean> - True if image is accessible
   */
  static async validateImageUrl(url: string, timeout: number = 5000): Promise<boolean> {
    if (!url || url.trim() === '') {
      return false;
    }

    return new Promise((resolve) => {
      const img = new Image();
      const timer = setTimeout(() => {
        img.src = ''; // Cancel loading
        resolve(false);
      }, timeout);

      img.onload = () => {
        clearTimeout(timer);
        resolve(true);
      };

      img.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };

      img.src = url;
    });
  }

  /**
   * Validate multiple image URLs in parallel
   * @param urls - Array of image URLs to validate
   * @param timeout - Timeout in milliseconds per image (default: 5000)
   * @returns Promise<boolean[]> - Array of validation results
   */
  static async validateImageUrls(urls: string[], timeout: number = 5000): Promise<boolean[]> {
    return Promise.all(urls.map(url => this.validateImageUrl(url, timeout)));
  }

  /**
   * Filter valid image URLs from an array
   * @param urls - Array of image URLs to filter
   * @param timeout - Timeout in milliseconds per image (default: 5000)
   * @returns Promise<string[]> - Array of valid URLs
   */
  static async filterValidImageUrls(urls: string[], timeout: number = 5000): Promise<string[]> {
    const validationResults = await this.validateImageUrls(urls, timeout);
    return urls.filter((_, index) => validationResults[index]);
  }
}
